import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Target, Eye, Award, ArrowRight, Check, Users, Briefcase, Globe } from 'lucide-react';
import AnimatedCounter from '../components/ui/AnimatedCounter';

const AboutPage = () => {
  const stats = [
    { value: '100', suffix: '%', label: 'Real-time Interaction' },
    { value: '100', suffix: '%', label: 'Innovation Impact' },
    { value: '1', suffix: 'M+', label: 'Satisfied Users Served' }
  ];

  const values = [
    {
      icon: Target,
      title: 'Envision Total Excellence',
      description: 'Design robust software architecture, strategic IT roadmaps, and cutting-edge solutions that match your unique business model while boosting organizational efficiency.'
    },
    {
      icon: Users,
      title: 'Immersive Development Process',
      description: 'Erase distractions from complex requirements, fine-tune development protocols, and manage every detail of your project quickly, all under one roof with AMTB Solution.'
    },
    {
      icon: Globe,
      title: 'Flexible & Custom Integration',
      description: 'We personalize every solution layer, ensuring your infrastructure is unique, agile, and precisely tailored to the scale of your business.'
    }
  ];

  const faqs = [
    {
      question: 'What technologies does AMTB Solution specialize in?',
      answer: 'We specialize in modern web and mobile technologies including React, Node.js, Python, AWS, Google Cloud, and various AI/ML frameworks.'
    },
    {
      question: 'Can you handle projects of any scale?',
      answer: 'Yes, our solutions are scalable – whether you\'re a growing startup or a large enterprise, we have solutions that adapt to your needs.'
    },
    {
      question: 'What if my business scales quickly?',
      answer: 'Our architectures are designed to scale – adding users, features, and services without downtime or major restructuring.'
    },
    {
      question: 'Do you provide post-deployment support?',
      answer: 'Yes, we offer 24/7 support, updates, bug fixes, and feature enhancements as part of our maintenance packages.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#B8336A]/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#2D6A6A]/20 blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Securing Your Digital Future.
                <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Empowering Your Business Growth</span>
              </h1>
              <p className="text-gray-300 text-lg mb-8">
                AMTB Solution is an IT services and software development company helping growing and enterprise organizations build, manage, and scale their digital environments.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold hover:shadow-lg transition-all"
              >
                Explore Our Services
                <ArrowRight size={18} />
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10">
                  <div className="text-3xl font-bold text-white mb-2">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-sm font-medium text-[#B8336A] mb-4">
                Our Mission at AMTB Solution
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Rethink How to Build, Scale, and Innovate
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                At AMTB Solution - We rethink how to develop software, analyze business needs, and use the resulting insights to improve decision-making. Our approach is transforming every sector, empowering you to strategize and build solutions whenever you need.
              </p>
              <p className="text-gray-600 leading-relaxed">
                In the rapidly evolving landscape of digital transformation, businesses face the pressing challenge of staying competitive and exceeding customer expectations. Traditional approaches are proving inadequate for satisfying the demand for agile, scalable, and innovative solutions.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600"
                alt="Team collaboration"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-2xl p-6 shadow-xl">
                <div className="text-3xl font-bold">10+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Driving Business Success with
              <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Next-Generation Solutions</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 flex items-center justify-center mb-6">
                  <value.icon size={28} className="text-[#B8336A]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="group bg-gray-50 rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="ml-4 flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center text-white group-open:rotate-180 transition-transform">
                    <ArrowRight size={16} className="rotate-90" />
                  </span>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
